import React, { useEffect, useState } from 'react';
import { Sparkles, X, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { prepChargeAPI } from '@/services/paymentsAPI';

/**
 * PrepPromoBanner — thin, full-width bar fixed at the top of the viewport.
 * When visible, it toggles `body.banner-active`, which globally pushes the
 * entire page (including the mobile top bar) down by 40px via CSS transition.
 * Auto-hides after 10 seconds; clicking anywhere on the strip opens the
 * Interview Prep simulator. Shown at most once per login session.
 */
const SESSION_KEY = 'numuv_prep_promo_seen';
const VISIBLE_MS = 10000;
const COLLAPSE_MS = 500;

export default function PrepPromoBanner() {
  const navigate = useNavigate();
  const [mounted, setMounted] = useState(false);
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (sessionStorage.getItem(SESSION_KEY) === '1') return;
    let alive = true;
    let hideTimer, unmountTimer;
    (async () => {
      try {
        const cfg = await prepChargeAPI.getConfig();
        if (!alive) return;
        if (cfg?.prep_promo_active && cfg?.prep_promo_message) {
          setMsg(cfg.prep_promo_message);
          setMounted(true);
          sessionStorage.setItem(SESSION_KEY, '1');
          requestAnimationFrame(() => {
            document.body.classList.add('banner-active');
            setOpen(true);
          });
          hideTimer = setTimeout(() => {
            document.body.classList.remove('banner-active');
            setOpen(false);
          }, VISIBLE_MS);
          unmountTimer = setTimeout(() => setMounted(false), VISIBLE_MS + COLLAPSE_MS);
        }
      } catch (_) { /* silent */ }
    })();
    return () => {
      alive = false;
      if (hideTimer) clearTimeout(hideTimer);
      if (unmountTimer) clearTimeout(unmountTimer);
      document.body.classList.remove('banner-active');
    };
  }, []);

  if (!mounted) return null;

  const close = (e) => {
    if (e) e.stopPropagation();
    document.body.classList.remove('banner-active');
    setOpen(false);
    setTimeout(() => setMounted(false), COLLAPSE_MS);
  };

  const openPrep = () => {
    navigate('/applicant/prep');
    close();
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={openPrep}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openPrep(); } }}
      className="fixed left-0 right-0 z-[90] h-10 flex items-center justify-center gap-3 px-4 cursor-pointer bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500 shadow-md transition-[top,transform,opacity] ease-out"
      style={{
        top: open ? '0px' : '-40px',
        opacity: open ? 1 : 0,
        transitionDuration: `${COLLAPSE_MS}ms`,
      }}
      data-testid="prep-promo-banner"
    >
      <Sparkles className="w-4 h-4 text-white flex-shrink-0" />
      <p className="text-white font-semibold text-xs sm:text-sm truncate" data-testid="prep-promo-message">
        {msg}
      </p>
      <span
        className="hidden sm:inline-flex items-center gap-1 text-white/95 text-xs font-medium"
        data-testid="prep-promo-cta"
      >
        Start prep <ChevronRight className="w-3.5 h-3.5" />
      </span>
      <button
        onClick={close}
        aria-label="Dismiss promo"
        className="absolute right-2 text-white/85 hover:text-white p-1"
        data-testid="prep-promo-close"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
